import { api } from './client'

export interface CrmTask {
  id: number
  title: string
  description?: string
  status: string
  priority: string | null
  dueDate: string | null
  assignedToId: number | null
  contractId?: number | null
  leadId?: number | null
  customerId?: number | null
  /** TASK-0167 (REQ-023-001): links to Operator (Catalog module) / Contest (Settings module) --
   * same optional/nullable treatment as `contractId`/`leadId`/`customerId` above, no FK, no
   * cross-module existence check, matching `CrmTask.OperatorId`/`ContestId` on the backend. */
  operatorId?: number | null
  contestId?: number | null
  /** REQ-023-001: links to Batch (Contracts module, "Lotto") -- same optional/nullable, no-FK
   * treatment as `operatorId`/`contestId` above, matching `CrmTask.BatchId` on the backend. */
  batchId?: number | null
  createdAt: string
}

/**
 * TASK-0186 (REQ-023-002): one row of a task's append-only comments — mirrors the backend's
 * `TaskComment` (Domain/TaskComment.cs), modeled directly on `ContractNote` (`api/contracts.ts`)
 * minus `isInternal`: REQ-023-002 asks for a plain "Comments" field on Task, with no internal/
 * external distinction.
 */
export interface TaskComment {
  id: number
  taskId: number
  content: string
  authorId?: number
  createdAt: string
}

/**
 * TASK-0219 (REQ-023-002): one row of a task's checklist -- mirrors the backend's
 * `TaskChecklistItem` (Domain/TaskChecklistItem.cs). Unlike `TaskComment` above, this entity is
 * mutable (`isDone` can be toggled, and the row itself can be deleted) -- see
 * `TaskChecklistItem`'s doc comment for why it is NOT copied append-only from `TaskComment`.
 */
export interface TaskChecklistItem {
  id: number
  taskId: number
  text: string
  isDone: boolean
  createdAt: string
}

export interface Appointment {
  id: number
  title: string
  description?: string
  startAt: string
  endAt: string
  status: string
  location: string | null
  /** TASK-0154 (REQ-079-001): the appointment's modality, one of `APPOINTMENT_TYPES` in
   * `OperationsPage.tsx`, or `null`/absent when none was specified -- always accepted, never
   * mandatory. */
  type?: string | null
  /** TASK-0185 (REQ-078-001): free text, no vocabulary (unlike `type` above) -- always accepted,
   * `null`/absent included. */
  sector?: string | null
  /** TASK-0185 (REQ-078-001): see `sector` above -- same free-text, no-vocabulary treatment. */
  productOfInterest?: string | null
  assignedToId: number | null
  contractId?: number | null
  leadId?: number | null
  customerId?: number | null
  createdAt: string
}

export interface TaskCreateRequest {
  title: string
  description?: string
  priority?: string
  dueDate?: string
  assignedToId?: number
  contractId?: number
  leadId?: number
  customerId?: number
  /** TASK-0167 (REQ-023-001): see `CrmTask.operatorId`/`contestId` above. */
  operatorId?: number
  contestId?: number
  /** REQ-023-001: see `CrmTask.batchId` above. */
  batchId?: number
}

/**
 * TASK-0075: shape returned by `GET /api/tasks/{id}` (`TasksController.Get`, which
 * `Ok()`s the full `CrmTask` entity, unlike `List`'s narrower projection below).
 * Deliberately used to prefill the edit form instead of the `CrmTask` list-row
 * shape: `TasksController.List`'s `.Select(...)` projection omits `Description`
 * entirely (only `Id/Title/Status/Priority/DueDate/AssignedToId/ContractId/
 * LeadId/CustomerId/OperatorId/ContestId/CreatedAt`), so a list row's `description` is always
 * `undefined` regardless of the real value. Building an edit payload from the
 * list row would send `description: null` for tasks that actually have a
 * description, wiping it — the exact class of bug this task exists to close.
 */
export interface CrmTaskDetail {
  id: number
  title: string
  description: string | null
  status: string
  priority: string
  dueDate: string | null
  completedAt: string | null
  assignedToId: number | null
  contractId: number | null
  leadId: number | null
  customerId: number | null
  /** TASK-0167 (REQ-023-001): see `CrmTask.operatorId`/`contestId` above. */
  operatorId: number | null
  contestId: number | null
  /** REQ-023-001: see `CrmTask.batchId` above. */
  batchId: number | null
  createdAt: string
  updatedAt?: string | null
}

/**
 * TASK-0075: matches `TasksController.TaskUpdateRequest` on the backend exactly.
 * `description`/`dueDate`/`contractId`/`leadId`/`customerId` are deliberately
 * REQUIRED (not optional) here, even though they're all nullable — this mirrors
 * `CrmTask.UpdateDetails`'s asymmetric guards (`platform/Backend/.../Domain/CrmTask.cs`,
 * lines 48-60): those five fields are unconditionally overwritten server-side
 * (no `if (x is not null)` guard, unlike `title`/`priority`/`assignedToId`), so
 * omitting one from the request body serializes it as `undefined` -> dropped by
 * `JSON.stringify` -> deserializes server-side as the record's default `null` ->
 * silently blanks that field even if the caller never touched it. Making them
 * required at the type level forces every call site to consciously pass the
 * task's current value (or an intentional new one), not just the fields the
 * user actually edited.
 *
 * TASK-0167 (REQ-023-001): `operatorId`/`contestId` join this same REQUIRED-but-nullable
 * group -- `UpdateDetails`'s new trailing `operatorId`/`contestId` parameters have no
 * `.HasValue` guard either, same full-replace treatment as `contractId`/`leadId`/`customerId`.
 *
 * REQ-023-001: `batchId` is deliberately OPTIONAL here (not required, unlike the fields above) --
 * `TaskUpdateRequest.BatchId` on the backend carries a trailing `= null` default (it was added
 * after this record already had real callers), so omitting it from the request body deserializes
 * server-side as `null`, matching the type here exactly rather than silently disagreeing with it.
 */
export interface TaskUpdateRequest {
  title?: string
  description: string | null
  priority?: string
  dueDate: string | null
  assignedToId?: number
  contractId: number | null
  leadId: number | null
  customerId: number | null
  operatorId: number | null
  contestId: number | null
  batchId?: number | null
}

export interface AppointmentCreateRequest {
  title: string
  description?: string
  startAt: string
  endAt: string
  location?: string
  assignedToId?: number
  contractId?: number
  leadId?: number
  customerId?: number
  /** TASK-0154 (REQ-079-001): optional, matches `Appointment.Create`'s new trailing `type = null`
   * parameter on the backend -- omitting it is a normal, accepted request (no type specified). */
  type?: string | null
  /** TASK-0185 (REQ-078-001): optional, matches `Appointment.Create`'s new trailing `sector = null`
   * parameter -- free text, no vocabulary, omitting it is a normal, accepted request. */
  sector?: string | null
  /** TASK-0185 (REQ-078-001): see `sector` above. */
  productOfInterest?: string | null
}

/**
 * TASK-0076: shape returned by `GET /api/appointments/{id}` (`AppointmentsController.Get`,
 * which `Ok()`s the full `Appointment` entity), used to prefill the edit form -- same reason
 * as `CrmTaskDetail` above (TASK-0075): `AppointmentsController.List`'s `.Select(...)`
 * projection omits `Description` entirely, so a list row's `description` is always
 * `undefined` regardless of the real value. Building an edit payload from the list row would
 * send `description: null` for appointments that actually have one, wiping it.
 */
export interface AppointmentDetail {
  id: number
  title: string
  description: string | null
  startAt: string
  endAt: string
  status: string
  location: string | null
  /** TASK-0154 (REQ-079-001): see `Appointment.type` above. */
  type?: string | null
  /** TASK-0185 (REQ-078-001): see `Appointment.sector` above. */
  sector?: string | null
  /** TASK-0185 (REQ-078-001): see `Appointment.productOfInterest` above. */
  productOfInterest?: string | null
  assignedToId: number | null
  contractId: number | null
  leadId: number | null
  customerId: number | null
  createdAt: string
  updatedAt?: string | null
}

/**
 * TASK-0076: matches `AppointmentsController.AppointmentUpdateRequest` on the backend exactly.
 * Unlike `TaskUpdateRequest`, this is simpler: `title`/`startAt`/`endAt` are non-nullable on
 * the backend record (always required), and `Appointment.UpdateDetails` has no asymmetric
 * guards -- it unconditionally overwrites `Description`/`Location` from whatever the request
 * carries, same as `title`/`startAt`/`endAt`. `description`/`location` are still typed as
 * required-but-nullable here (not optional) so every call site is forced to consciously pass
 * the appointment's current value -- omitting the key from the JSON body would deserialize
 * server-side as `null` and silently blank a description/location the user never touched.
 *
 * TASK-0154 (REQ-079-001) adds `type` as OPTIONAL (`type?: string | null`), unlike
 * `description`/`location` above -- deliberately, and safely: `UpdateDetails`'s new `type`
 * parameter is required-but-nullable on the backend just like `description`/`location`, but
 * `type` itself is never mandatory data (the requirement, and `Appointment.cs`'s validation,
 * accept `null` unconditionally), so there is no "silently blank a value the user never touched"
 * hazard here the way there would be for a field that is supposed to hold real, meaningful data
 * -- and the one call site in `OperationsPage.tsx` always sends it explicitly regardless.
 */
export interface AppointmentUpdateRequest {
  title: string
  description: string | null
  startAt: string
  endAt: string
  location: string | null
  type?: string | null
  /** TASK-0185 (REQ-078-001): optional, same reasoning as `type` immediately above -- free text,
   * no vocabulary, never mandatory. */
  sector?: string | null
  /** TASK-0185 (REQ-078-001): see `sector` above. */
  productOfInterest?: string | null
}

export function fetchTasks(
  // TASK-0187 (REQ-023-003): `dueFilter` mirrors `status` -- optional, same style, sent only when
  // truthy. `TasksController.List` accepts exactly "overdue"/"today"/"upcoming" and 400s on any
  // other non-empty value, so callers must pass one of those three (or omit it).
  // TASK-0196 (REQ-023-003): `teamOnly` -- a plain boolean, not a closed vocabulary like
  // `dueFilter`, so it is sent as the literal string 'true' only when `true` and otherwise
  // omitted entirely (never an explicit 'false'), same "omit when falsy" convention `status`/
  // `dueFilter` already use above.
  // F-101 (REQ-277-001): `sort` = created_desc (default) | due_asc | due_desc | priority | status;
  // `priority` = urgent|high|normal|low, filtrata dal server (prima era filtrata sulla sola pagina aperta).
  params?: { status?: string; dueFilter?: string; teamOnly?: boolean; page?: number; sort?: string; priority?: string }
): Promise<{ total: number; page: number; pageSize: number; items: CrmTask[] }> {
  const p = new URLSearchParams()
  if (params?.status) p.set('status', params.status)
  if (params?.sort) p.set('sort', params.sort)
  if (params?.priority) p.set('priority', params.priority)
  if (params?.dueFilter) p.set('dueFilter', params.dueFilter)
  if (params?.teamOnly) p.set('teamOnly', 'true')
  if (params?.page) p.set('page', String(params.page))
  const qs = p.toString()
  return api(`/api/tasks${qs ? `?${qs}` : ''}`)
}

export function fetchAppointments(
  // F-101: `sort` = start_asc (default) | start_desc | status.
  params?: { status?: string; page?: number; sort?: string }
): Promise<{ total: number; page: number; pageSize: number; items: Appointment[] }> {
  const p = new URLSearchParams()
  if (params?.status) p.set('status', params.status)
  if (params?.sort) p.set('sort', params.sort)
  if (params?.page) p.set('page', String(params.page))
  const qs = p.toString()
  return api(`/api/appointments${qs ? `?${qs}` : ''}`)
}

export const tasksApi = {
  list: (p?: { status?: string; page?: number; size?: number }) => {
    const q = new URLSearchParams()
    if (p?.status) q.set('status', p.status)
    q.set('page', String(p?.page ?? 1))
    q.set('size', String(p?.size ?? 20))
    return api<{ total: number; page: number; size: number; items: CrmTask[] }>(`/api/tasks?${q}`)
  },
  create: (data: TaskCreateRequest) =>
    api<{ id: number }>('/api/tasks', { method: 'POST', body: JSON.stringify(data) }),
  get: (id: number) => api<CrmTaskDetail>(`/api/tasks/${id}`),
  update: (id: number, data: TaskUpdateRequest) =>
    api<void>(`/api/tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  complete: (id: number) =>
    api<void>(`/api/tasks/${id}/complete`, { method: 'POST' }),
  /** F-104 (REQ-287-001): non cancella: sposta nel cestino (`DELETE ?reason=`, Admin). Si ripristina da Cestino. */
  delete: (id: number, reason?: string) =>
    api<void>(`/api/tasks/${id}${reason && reason.trim() ? `?reason=${encodeURIComponent(reason.trim())}` : ''}`, { method: 'DELETE' }),
  // TASK-0186 (REQ-023-002): same style as `contractsApi.getNotes`/`.addNote` (`api/contracts.ts`).
  // Unlike that pair, `addComment` types its response as the real `TaskComment`
  // `TasksController.AddComment` returns (`Ok(comment)`), not just `{ id }` --
  // `ContractsController.AddNote` returns only `{ note.Id }` while `contractsApi.addNote` is typed
  // (and used, in `ContractDetailPage.tsx`'s `handleAddNote`) as if it returned the full note; this
  // does not replicate that pre-existing mismatch.
  getComments: (id: number) => api<TaskComment[]>(`/api/tasks/${id}/comments`),
  addComment: (id: number, content: string) =>
    api<TaskComment>(`/api/tasks/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    }),
  // TASK-0219 (REQ-023-002): same style as `getComments`/`addComment` above, plus the two mutable
  // operations `TaskComment` doesn't have -- `setChecklistItemDone` (PATCH, toggles `isDone`) and
  // `deleteChecklistItem` (DELETE), matching `TasksController`'s 4 new checklist actions exactly.
  getChecklist: (id: number) => api<TaskChecklistItem[]>(`/api/tasks/${id}/checklist`),
  addChecklistItem: (id: number, text: string) =>
    api<TaskChecklistItem>(`/api/tasks/${id}/checklist`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),
  setChecklistItemDone: (id: number, itemId: number, isDone: boolean) =>
    api<void>(`/api/tasks/${id}/checklist/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify({ isDone }),
    }),
  deleteChecklistItem: (id: number, itemId: number) =>
    api<void>(`/api/tasks/${id}/checklist/${itemId}`, { method: 'DELETE' }),
}

export const appointmentsApi = {
  list: (p?: { status?: string; from?: string; to?: string; page?: number; size?: number }) => {
    const q = new URLSearchParams()
    if (p?.status) q.set('status', p.status)
    if (p?.from) q.set('from', p.from)
    if (p?.to) q.set('to', p.to)
    q.set('page', String(p?.page ?? 1))
    q.set('size', String(p?.size ?? 20))
    return api<{ total: number; page: number; size: number; items: Appointment[] }>(`/api/appointments?${q}`)
  },
  create: (data: AppointmentCreateRequest) =>
    api<{ id: number }>('/api/appointments', { method: 'POST', body: JSON.stringify(data) }),
  get: (id: number) => api<AppointmentDetail>(`/api/appointments/${id}`),
  update: (id: number, data: AppointmentUpdateRequest) =>
    api<void>(`/api/appointments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  cancel: (id: number) =>
    api<void>(`/api/appointments/${id}/cancel`, { method: 'POST' }),
  /**
   * TASK-0188 (REQ-080-001): `confirmed`/`no_show` were already defined in `apptStatusColors`/
   * `apptStatusLabels` (`OperationsPage.tsx`) but unreachable — nothing ever called
   * `Appointment.Confirm()`/`MarkNoShow()` on the backend. Same bodyless-POST shape as `cancel`
   * above, its direct template.
   */
  confirm: (id: number) =>
    api<void>(`/api/appointments/${id}/confirm`, { method: 'POST' }),
  /** TASK-0188 (REQ-080-001): see `confirm` immediately above. Route is kebab-case (`no-show`),
   * matching `AppointmentsController`'s `[HttpPost("{id:long}/no-show")]`. */
  markNoShow: (id: number) =>
    api<void>(`/api/appointments/${id}/no-show`, { method: 'POST' }),
  /**
   * TASK-0251 (REQ-080-001): the remaining 4 of the 9 states the requirement lists, completing the
   * vocabulary `confirm`/`markNoShow` above left partial. Same bodyless-POST shape as `confirm`/
   * `markNoShow`, their direct templates. Routes are kebab-case, dropping the "Mark" prefix the C#
   * action names keep, same as `markNoShow`'s `no-show` route.
   */
  markToConfirm: (id: number) =>
    api<void>(`/api/appointments/${id}/to-confirm`, { method: 'POST' }),
  /** TASK-0251 (REQ-080-001): see `markToConfirm` immediately above. One of the two differentiated
   * cancellation actions — `cancel` above stays untouched and keeps writing the undifferentiated
   * `cancelled` value. */
  cancelByCustomer: (id: number) =>
    api<void>(`/api/appointments/${id}/cancel-by-customer`, { method: 'POST' }),
  /** TASK-0251 (REQ-080-001): see `cancelByCustomer` immediately above, the other differentiated
   * cancellation action. */
  cancelByAgent: (id: number) =>
    api<void>(`/api/appointments/${id}/cancel-by-agent`, { method: 'POST' }),
  /** TASK-0251 (REQ-080-001): see `markToConfirm` above. */
  markRescheduled: (id: number) =>
    api<void>(`/api/appointments/${id}/rescheduled`, { method: 'POST' }),
  /** TASK-0251 (REQ-080-001): see `markToConfirm` above, the last of the 9 states. */
  markToRecall: (id: number) =>
    api<void>(`/api/appointments/${id}/to-recall`, { method: 'POST' }),
  /**
   * TASK-0153 (REQ-081-001): completing an appointment now REQUIRES an outcome from the applied
   * vocabulary (`AppointmentOutcomes` in `Appointment.cs`, mirrored character-for-character by
   * `APPOINTMENT_OUTCOMES` in `OperationsPage.tsx`). The backend answers 400 — with a message
   * naming the valid values — for a missing, blank or out-of-vocabulary `outcome`, so this is no
   * longer a bodyless POST. `outcomeReason` is the requirement's optional free-text field.
   */
  complete: (id: number, outcome: string, outcomeReason?: string) =>
    api<void>(`/api/appointments/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify({ outcome, outcomeReason }),
    }),
  /** F-104 (REQ-287-001): non cancella: sposta nel cestino (`DELETE ?reason=`, Admin). Si ripristina da Cestino. */
  delete: (id: number, reason?: string) =>
    api<void>(`/api/appointments/${id}${reason && reason.trim() ? `?reason=${encodeURIComponent(reason.trim())}` : ''}`, { method: 'DELETE' }),
}
